'use client'

import { Box, Button, Container, Typography } from '@mui/material'
import React from 'react'

import promotionBanner from '@/src/components/common/home/flightSearch/mobileAppPromotion/assets/promotion.png'
import LazyImage from '@/src/components/common/ui/LazyImage'
import {
    AirlineIcon,
    CustomerSupportIcon,
    FastBookingIcon,
    SecurePaymentIcon
} from '@/src/constants/homepagePromoIcons'
import { TranslationFunction } from '@/src/types/translations'
import FeatureRenderer from '../../../featureRenderer'

interface MobileAppPromotionProps {
    t: TranslationFunction<'homePage.promotion'>
    tFooter: TranslationFunction<'footer'>
    isRtl: boolean
    verticalLayout?: boolean
}

interface FeatureItem {
    icon: React.ReactNode
    text: string
}

const MobileAppPromotion: React.FC<MobileAppPromotionProps> = ({ t, tFooter, isRtl, verticalLayout = false }) => {
    const features: FeatureItem[] = [
        { icon: <CustomerSupportIcon />, text: tFooter('customerSupport247') },
        { icon: <FastBookingIcon />, text: tFooter('easyFastBooking') },
        { icon: <SecurePaymentIcon />, text: tFooter('securePaymentOptions') },
        { icon: <AirlineIcon />, text: tFooter('airlineOptions') }
    ]

    if (verticalLayout) {
        return <VerticalLayout features={features} />
    }

    return <HorizontalLayout t={t} isRtl={isRtl} features={features} />
}

const HorizontalLayout: React.FC<{
    t: TranslationFunction<'homePage.promotion'>
    isRtl: boolean
    features: FeatureItem[]
}> = ({ t, isRtl, features }) => {
    return (
        <Box
            sx={{
                // bgcolor: 'grey.50',
                mt: { xs: 4, sm: 6 },
                mb: { xs: 4, sm: 6 },
                py: { xs: 4, sm: 6, md: 0 },
                display: { xs: 'none', sm: 'none', md: 'block' }
            }}
        >
            <FeatureRenderer type="APP_DOWNLOAD_BANNER">
                <Container maxWidth="xl">
                    {/* Mobile App Promotional Banner */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, rgb(67, 129, 205) 0%, rgb(25, 77, 145) 100%)',
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex'
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                maxWidth: 550,
                                height: '100%',
                                position: 'relative',
                                aspectRatio: '16/9'
                            }}
                        >
                            <LazyImage
                                src={
                                    promotionBanner.src ||
                                    (typeof promotionBanner === 'string' ? promotionBanner : String(promotionBanner))
                                }
                                alt="App promotion"
                                sizes="(max-width: 768px) 100vw, 550px"
                                objectFit="cover"
                            />
                        </Box>
                        <Box sx={{ flex: 1, position: 'relative', zIndex: 2, pt: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    minHeight: 180,
                                    height: "100%",
                                    justifyContent: "center"
                                }}
                            >
                                <Box sx={{ width: '100%', mb: 4 }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            color: 'white',
                                            fontWeight: 500,
                                            mb: 2,
                                            fontSize: 20
                                        }}
                                    >
                                        {t('title')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            color: 'common.white',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2
                                        }}
                                    >
                                        <Box sx={{ flexDirection: 'column' }}>
                                            <Typography
                                                fontWeight={600}
                                                fontSize={{ xs: '1rem', sm: '1.25rem', md: '1.5rem' }}
                                            >
                                                4.8
                                            </Typography>
                                            <Typography>{t('ratings')}</Typography>
                                        </Box>
                                        <Box sx={{ flexDirection: 'column' }}>
                                            <Typography
                                                fontWeight={600}
                                                fontSize={{ xs: '1rem', sm: '1.25rem', md: '1.5rem' }}
                                            >
                                                1M+
                                            </Typography>
                                            <Typography>{t('downloads')}</Typography>
                                        </Box>
                                    </Box>

                                    {/* App Download Buttons */}
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <AppStoreButton isRtl={isRtl} />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </FeatureRenderer>

            {/* Feature Highlights */}
            <Box sx={{ mt: { xs: 6, sm: 8 } }}>
                <Container maxWidth="xl">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                            overflow: 'hidden',
                            height: { xs: 'auto', sm: '164px' },
                            border: "1px solid #E4E7EB"
                            // boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                    >
                        {features.map((feature, index) => (
                            <HorizontalFeatureItem
                                key={index}
                                feature={feature}
                                isLast={index === features.length - 1}
                            />
                        ))}
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

const VerticalLayout: React.FC<{ features: FeatureItem[] }> = ({ features }) => {
    return (
        <Box
            sx={{
                bgcolor: 'transparent',
                display: 'flex',
                height: '100%',
                minHeight: '100%',
                flexDirection: 'column',
                width: '100%'
            }}
        >
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    width: '100%'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        height: '100%'
                    }}
                >
                    {features.map((feature, index) => (
                        <VerticalFeatureItem key={index} feature={feature} isLast={index === features.length - 1} />
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

const HorizontalFeatureItem: React.FC<{ feature: FeatureItem; isLast: boolean }> = ({ feature, isLast }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 3, sm: 2 },
                position: 'relative',
                borderBottom: { xs: '1px solid', sm: 'none' },
                borderColor: 'divider',
                minHeight: { xs: '120px', sm: 'auto' },
                '&::after': {
                    content: { xs: 'none', sm: '""' },
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '60%',
                    width: '1px',
                    bgcolor: 'rgba(0, 0, 0, 0.12)',
                    display: isLast ? 'none' : { xs: 'none', sm: 'block' }
                },
                '& svg': {
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>{feature.icon}</Box>
            <Typography
                sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center'
                }}
            >
                {feature.text}
            </Typography>
        </Box>
    )
}

const VerticalFeatureItem: React.FC<{ feature: FeatureItem; isLast: boolean }> = ({ feature, isLast }) => {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                p: 1.5,
                borderBottom: isLast ? 'none' : '1px solid',
                borderColor: 'divider',
                gap: 1.5,
                '& svg': {
                    fontSize: '1.5rem',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{feature.icon}</Box>
            <Typography
                sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'left'
                }}
            >
                {feature.text}
            </Typography>
        </Box>
    )
}

const AppStoreButton: React.FC<{ isRtl: boolean }> = ({ isRtl }) => {
    return (
        <>
            {/* Google Play Button */}
            <Button
                href="https://play.google.com/store/apps/details?id=com.flyin.bookings"
                target="_blank"
                className="android"
                sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    px: 4,
                    py: 2,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    minWidth: 180,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: 'grey.100'
                    },
                    '&::before': {
                        content: '""',
                        display: 'inline-block',
                        height: '44px',
                        width: '100%',
                        background:
                            'url(https://www.flyin.com/common/themes/v2/img/appBtnSprite.png) -144px -63px / 320px no-repeat',
                        position: 'absolute',
                        top: '50%',
                        left: isRtl ? '45%' : '55%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                    }}
                />
            </Button>

            {/* App Store Button */}
            <Button
                href="https://itunes.apple.com/us/app/flyin.com-tyran-w-fnadq/id1106268318"
                target="_blank"
                className="ios"
                sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    px: 4,
                    py: 2,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    minWidth: 180,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: 'grey.100'
                    },
                    '&::before': {
                        content: '""',
                        display: 'inline-block',
                        height: '44px',
                        width: '100%',
                        background:
                            'url(https://www.flyin.com/common/themes/v2/img/appBtnSprite.png) -135px -9px / 320px no-repeat',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%'
                    }}
                />
            </Button>
        </>
    )
}

export { MobileAppPromotion }
export type { MobileAppPromotionProps }
